import { Box, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import Flex from "../../../../../../../components/Flex";
import Input from "../../../../../../../components/Input";
import Text from "../../../../../../../components/Text";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import { CARD_HOLDER_NAME_SCHEMA } from "../../../../../../../components/app/provider/billing/payment/payment-constant";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import { DisclosureHost } from "../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { useUpdatePatientCard } from "../../../../../../../queries/billings/payments/use-update-patient-card";
import { PatientCard } from "../../../../../../../services";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../../../utils/utils";
import { CARD_BRAND_ICONS } from "./PatientCardDetails";

// The grid row is a PatientCard plus extra BE-provided display fields. The list
// endpoint returns `patientName` and a pre-formatted `cardExpiry` rather than
// `cardHolderName`/`expMonth`/`expYear`.
type PatientCardRow = PatientCard & {
  cardExpiry?: string;
  patientName?: string;
};

interface EditCardModalProps {
  disclosureHost: DisclosureHost;
  card?: PatientCardRow;
}

interface EditCardForm {
  cardHolderName: string;
  expiryDate: string;
}

// Build the "MM/YYYY" expiry string the form edits. Prefer the numeric parts,
// otherwise normalise the BE-provided `cardExpiry` (which can be "M/YYYY").
const toExpiryString = (card?: PatientCardRow) => {
  if (card?.expMonth && card?.expYear) {
    return `${String(card.expMonth).padStart(2, "0")}/${card.expYear}`;
  }
  if (card?.cardExpiry) {
    const [mm, yy] = String(card.cardExpiry)
      .split("/")
      .map((part) => part.trim());
    if (mm && yy) return `${mm.padStart(2, "0")}/${yy}`;
    return card.cardExpiry;
  }
  return "";
};

// Parse "MM/YY" or "MM/YYYY" back into numeric month/year for the payload.
const parseExpiry = (value: string) => {
  const [mm, yy] = value.split("/").map((part) => part.trim());
  const expMonth = Number(mm);
  let expYear = Number(yy);
  if (yy?.length === 2) expYear = 2000 + expYear;
  return { expMonth, expYear };
};

const EditCardModal = ({ disclosureHost, card }: EditCardModalProps) => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: updateCard, isPending } = useUpdatePatientCard();

  const formik = useFormik<EditCardForm>({
    initialValues: {
      // The list payload returns `patientName`, not `cardHolderName`, so fall
      // back to it for the prefill.
      cardHolderName: card?.cardHolderName ?? card?.patientName ?? "",
      expiryDate: toExpiryString(card),
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      cardHolderName: CARD_HOLDER_NAME_SCHEMA,
      // Expiry is read-only, so it carries no validation of its own —
      // an unvalidatable value would block submit with no visible error.
    }),
    onSubmit: handleSubmit,
  });

  function close() {
    formik.resetForm();
    disclosureHost.close();
  }

  async function handleSubmit(values: EditCardForm) {
    if (!card?.uuid) return;
    const { expMonth, expYear } = parseExpiry(values.expiryDate);

    const payload: PatientCard = {
      ...card,
      cardHolderName: values.cardHolderName.trim(),
      // Keep whatever the card already carries if the display string can't be parsed.
      expMonth: Number.isFinite(expMonth) ? expMonth : card.expMonth,
      expYear: Number.isFinite(expYear) ? expYear : card.expYear,
    };

    try {
      const res = await updateCard({
        cardId: card.uuid,
        patientCard: payload,
        xTENANTID: getXtenantId(),
      });
      queryClient.invalidateQueries({ queryKey: ["get-patient-payment-v2"] });
      queryClient.invalidateQueries({ queryKey: ["get-patient-cards"] });
      showSnackbar(getSuccessSnackbar(res));
      close();
    } catch (err) {
      showSnackbar(getErrorSnackbar(err));
    }
  }

  const brandIcon = CARD_BRAND_ICONS[card?.displayBrand?.toLowerCase() ?? ""];

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={close}
      dialogTitle="Edit Card"
      dialogAction={
        <Flex justifyContent={"end"} gap={1}>
          <StyledButtonV2 label="Cancel" variant="outlined" onClick={close} />
          <StyledButtonV2
            label="Save"
            variant="contained"
            disabled={isPending}
            onClick={formik.submitForm}
          />
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "32vw",
        },
      }}
    >
      <Box pt={1}>
        <Grid container spacing={2}>
          {/* Masked, non-editable card identity */}
          <Grid item xs={12}>
            <Flex alignItems={"center"} gap={"12px"}>
              {brandIcon && (
                <img
                  width={30}
                  src={brandIcon}
                  alt={card?.displayBrand || "card"}
                  style={{ objectFit: "contain" }}
                />
              )}
              <Text
                isTabletext
                style={{ color: "#74797B", letterSpacing: "1px" }}
              >
                •••• •••• •••• {card?.last4 ?? "••••"}
              </Text>
            </Flex>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Input
              label="Card Holder Name"
              required
              error={
                formik.touched.cardHolderName && formik.errors.cardHolderName
              }
              textFieldProps={{
                placeholder: "Card Holder Name",
                name: "cardHolderName",
                value: formik.values.cardHolderName,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {/* Expiry is fixed by the card on file and stays read-only here. */}
            <Input
              label="Expiry Date"
              required
              clearable={false}
              textFieldProps={{
                placeholder: "MM/YYYY",
                name: "expiryDate",
                value: formik.values.expiryDate,
                disabled: true,
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </StyledFormModal>
  );
};

export default EditCardModal;
